import React from 'react';
import { Bot, User, Sparkles, Check } from 'lucide-react';
import type { ChatMessage as ChatMessageType } from '../../stores/aiChatStore';
import { messageDisplayText } from '../../utils/chatDisplay';
import { ChatMarkdown } from '../../utils/chatMarkdown';
import { ProposalPreview } from './ProposalPreview';

interface ChatMessageProps {
  message: ChatMessageType;
  onApplySuggestion?: (suggestion: any, messageId: string) => void;
}

export function ChatMessage({ message, onApplySuggestion }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const displayText = messageDisplayText(message.content, isUser);

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
      }`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={`flex-1 space-y-2 ${isUser ? 'flex flex-col items-end' : ''}`}>
        <div className={`rounded-2xl px-4 py-2 max-w-[85%] ${
          isUser
            ? 'bg-primary text-primary-foreground ml-auto'
            : 'bg-muted text-foreground'
        }`}>
          {/* ⚠️ ASSISTANT ONLY. A user's own message is rendered exactly as they
              typed it — turning their asterisks into emphasis would silently
              rewrite their words back at them. The model is the only author
              here that actually writes markdown. */}
          {isUser
            ? <p className="text-sm whitespace-pre-wrap break-words">{displayText}</p>
            : <ChatMarkdown text={displayText} />}
        </div>

        {message.proposals?.map((p) => (
          <ProposalPreview key={p.id} proposal={p} />
        ))}

        {/* ⚠️⚠️ ONCE APPLIED, THE BUTTON MUST NOT COME BACK. Applying charges
            Coin and creates real items, so a live button on an already-applied
            suggestion is a second charge and a duplicate set of items — the
            user's own history invites them to pay twice. */}
        {message.suggestions && message.suggestionAppliedAt && (
          <span
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium
              bg-muted text-muted-foreground rounded-lg w-fit"
            title={`Applied ${new Date(message.suggestionAppliedAt).toLocaleString()}`}
          >
            <Check className="w-3 h-3" />
            Applied
          </span>
        )}

        {message.suggestions && !message.suggestionAppliedAt && onApplySuggestion && (
          <button
            onClick={() => onApplySuggestion(message.suggestions, message.id)}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium
              bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
          >
            <Sparkles className="w-3 h-3" />
            Apply Suggestion
          </button>
        )}

        <p className="text-xs text-muted-foreground px-1">
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}
